<script setup>

const props = defineProps({
  sharedContent: {
    type: Object,
    default: null
  },
  location: Object,
  leadGen: Object,
  shortForm: Object,
  trustPilotReviews: Object
})
const { urlRegisterFunction } = useUtils();
const description = props.location.description ? props.location.description : props.sharedContent.sharedDescription;
const heroGallery = props.location.heroGallery ? props.location.heroGallery : props.sharedContent.sharedHeroGallery;

const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"
];
 
const from = new Date(new Date(props.location.pulseLocation.firstSessionStartDate).toLocaleString('en', {timeZone: 'UTC'}));
const to = new Date(new Date(props.location.pulseLocation.lastSessionEndDate).toLocaleString('en', {timeZone: 'UTC'}));
const showShortLeadForm = props.location.pulseLocation.status.typeId !== 1;
const { altFallback } = usePageAltFallback();
const mainImageAlt = computed(() =>
  resolveMediaAlt(props.location?.listingImage, altFallback.value, 'Location image')
);
const getGalleryImageAlt = (image) =>
  resolveMediaAlt(image, altFallback.value, 'Location image');

const goToAVC = () => {
  // Scroll to element
  const offset = document.getElementById('www-spa-container').offsetTop;
  const deviceOffset = screen.width >= 992 ? 80 : 0;
  const scrollTopTo = offset - 130 - deviceOffset;  
  window.scrollTo({
    top: scrollTopTo,
    behavior: 'smooth',
  });
}

const goToReviews = () => {
  const parent = document.getElementById('trust-pilot-reviews').closest('section');
  const sibling = parent.previousElementSibling;
  const offset = sibling.offsetTop;
  const deviceOffset = 70;
  const scrollTopTo = offset - 130 - deviceOffset;

  window.scrollTo({
    top: scrollTopTo,
    behavior: 'smooth',
  });
}

</script>

<template>
  <div class="block hero-section container" id="hero-section">
    <div class="row">
      <div class="col-12">
        <h1>{{ location.name }}</h1>
      </div>
      <div class="col-12 ">
        <span v-if="location.showTrustPilotHero">
          <span class="icon-star me-2"></span>
          <span>{{ trustPilotReviews.averageRating }}</span>
          <span class="separator mx-2">•</span>
          <span>
            <button class="btn btn-link fw-bold" @click="goToReviews">{{ Intl.NumberFormat('en-US').format(trustPilotReviews.totalReviews) }} reviews</button>
          </span>
        </span>        
        <span class="separator mx-2" v-if="location.showTrustPilotHero && location.badge">•</span>
        <span v-if="location.badge">
          <span class="me-2" >
            <UIIconsAward />
          </span>
          <span>Premier location</span>
        </span>
        
      </div>
    </div>
    <div class="row mt-4 justify-content-center gallery">
      <div class="col-md-6 col-12 main-image">
        <UIImage
          :class="'w-100 h-100 '"
          :src="location?.listingImage ? location?.listingImage?.url : 'https://via.placeholder.com/640x360'"
          :alt="mainImageAlt"
          loading="eager"
          fetchpriority="high"
        />
      </div>
      <div class="col-6 d-none d-md-block">
        <div class="row row-cols-2 g-4" >
          <div class="col" v-for="image in heroGallery.slice(0, 4)" :key="`gallery-${location.id}-${image.id}`" >
            <UIImage :class="'w-100 small '" :src="image.url" :alt="getGalleryImageAlt(image)" />
          </div>
        </div> 
      </div>
    </div>
    <div class="row justify-content-center position-relative cta-section" v-if="showShortLeadForm">
      <div class="col-lg-10 col-xs-12 col-md-10 align-self-center">
        <FormLocationShort :heading="shortForm.shortFormHeading" :thankYouMessage="shortForm.thankYouMessage" :leadGenFields="leadGen.leadGenFields" :submitButtonText="shortForm.shortFormSubmit" :locationName="location.pulseLocation.name" :zeroBounceValidation="shortForm.zeroBounceValidation" :customLeadGenMessage="shortForm.customLeadGenMessage"/>
      </div>
    </div>
    <div class="row justify-content-center position-relative cta-section" v-else>
      <div class="col-10 col-xl-4 col-lg-4 col-md-5 col-sm-8 align-self-center text-center cta-wrapper">
        <a class="btn btn-id-primary display w-100 d-none d-md-block" @click="goToAVC">Courses, prices & dates</a>
        <a class="btn btn-id-primary display w-100 d-block d-md-none" :href="urlRegisterFunction('?lid='+ location.pulseId, true)">Courses, prices & dates</a>
      </div>
    </div>

    <div class="row justify-content-center secondary-title">
      <div class="col-12">
        <UIHeading 
          v-if="location.secondaryTitle"
          :titleComponent="location.secondaryTitle" 
          :titleClass="'text-center ' + location.secondaryTitle.typography" 
        />
      </div>
      <div class="col-12 justify-content-center mt-2 mb-4 d-flex flex-column flex-md-row status-icons-wrapper" v-if="location.pulseLocation.status && location.pulseLocation.status.typeId == 1">
        <span class="me-md-4 me-0 text-center" v-if="location.pulseLocation.residencyOption">
          <span class="me-2" aria-label="Day camp available">
            <UIIconsSun />
          </span>
          <span>Day Camps</span>
        </span>
        <span class="me-md-4 me-0 text-center" v-if="location.pulseLocation.residencyOption && location.pulseLocation.residencyOption.name == 'Day & Overnight'">
          <span class="me-2" aria-label="Day & Overnight camp available">
            <UIIconsMoon />
          </span>
          <span>Overnight Camps</span>
        </span>
        <span class="text-center">
          <span class="me-2" aria-label="Date">
            <UIIconsCalendar />
          </span>
          <span>{{ `Running from ${monthNames[from.getMonth()]} ${from.getDate()} - ${monthNames[to.getMonth()]} ${to.getDate()}` }}</span>
        </span>
      </div>
      <div class="col-12 text-center" v-html="description">        
      </div>
    </div>
    
  </div>
</template>

<style lang="scss" scoped>
.hero-section {
  .gallery {
    height: auto;
    @include media-breakpoint-up(md) {
      height: 450px;
    }
    .main-image {
      img {
        object-fit: cover;
      }
      @include media-breakpoint-down(sm) {
        padding-left: 0px;
        padding-right: 0px;
        img {
          border-radius: 0px;
        }
      }
    }
    .small {
      height: 220px;
      object-fit: cover;
    }
  }
  
  
  span {
    font-weight: 600;    
  }
  button {
    &.btn-link {
      min-width: unset;
    }
  }
  .cta-section {
    top: -30px;
    .cta-wrapper{
      background-color: white;
      border-radius: 40px;
      padding: 20px;
      max-width: 320px;
    }
  }

  .secondary-title {
    color: $color-primary;
  }

  .status-icons-wrapper {
    svg {
      position: relative;
      top: -2px;
    }
  }
}
</style>